import { FormEvent, useEffect, useState } from 'react';
import { addTruthyParamsToUrl, addParamsToUrl } from '@zapier/url-utils';
import { urls } from '@zapier/identity-utils';

import {
  defaultRouteTo,
  discoverAccount,
  fetch,
  logout,
  nextForSsoRedirect,
} from '../../utils';
import { RouteTo } from '../../types';
import { GENERIC_ERROR_MESSAGE } from './constants';
import { DASHBOARD_FALLBACK_URL_NAME } from '../../constants';
import { parseLoginErrors } from './errorUtils';
import {
  LoginData,
  LoginErrors,
  LoginFieldFlags,
  LoginOutput,
  LoginResult,
  LoginResultType,
  LoginStatus,
  UseLoginInput,
} from './types';
import { storeAuthType, useAuthType } from '../../hooks';
import { shouldShowSSOOption } from './shouldShowSSOOption';
import { useNewDeviceCodeStep } from './useNewDeviceCodeStep';
import { captureMessage } from '@sentry/react';

const defaultState = {
  email: '',
  password: '',
  captcha: null,
};

const defaultFlagState = {
  captcha: false,
  email: false,
  password: false,
};

/**
 * Attempts login via API and handles errors / SAML flow
 */
async function login(
  data: LoginData,
  next: string | undefined,
  routeTo: RouteTo
): Promise<LoginOutput> {
  // First ensure we are logged out
  await logout();

  // Try to discovery the user's account
  const discoveredAccount = await discoverAccount({
    attemptStoredAccount: true,
    email: data.email,
  });

  // Check for SAML redirect from account-discovery
  if (discoveredAccount?.hasSamlSso && discoveredAccount?.ssoUrl) {
    // We need to redirect to start SAML
    routeTo(
      addTruthyParamsToUrl(discoveredAccount.ssoUrl, {
        email: data?.email,
        next: nextForSsoRedirect(next),
      })
    );

    return {
      didRedirect: true,
    };
  }

  // Attempt login
  const loginResult = await fetch<LoginResult>(urls.LOGIN_API_URL, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
    },
    body: {
      // If we found an account id from account-discovery
      account_id: discoveredAccount?.id,
      captcha: data.captcha,
      email: data.email,
      password: data.password,
    },
  });

  // Errors are in a hard to read format so reformat them
  // for easy consumption in the UI
  const errors = parseLoginErrors(loginResult);

  // See if we need to redirect the user
  if (errors.redirectUrl) {
    // Send errors to Sentry
    captureMessage('login errors', {
      tags: {
        section: 'login',
        step: data.password ? 'password' : 'email',
        http_status_code: loginResult.status ?? 'unknown',
        result_type: errors.type,
        redirect_url: errors.redirectUrl,
      },
    });

    // The verify-email page requires the user's email as a query param, so
    // thread it through only for that redirect. addTruthyParamsToUrl drops
    // falsy values, leaving every other redirect unchanged.
    const params: Record<string, string | undefined> = { next };
    if (errors.type === LoginResultType.IS_EMAIL_VERIFICATION_REQUIRED) {
      params.email = data.email;
    }

    routeTo(addTruthyParamsToUrl(errors.redirectUrl, params));
  }

  // If there were no errors, and we successfully logged in, store the user's authentication method, so we can automatically
  // show this authentication method next time they try to log in.
  if (Object.keys(errors.errors || {}).length === 0) {
    storeAuthType('email');
  }

  return {
    account: discoveredAccount,
    didRedirect: Boolean(errors.redirectUrl),
    ...errors,
  };
}

function getDefaultState(input: UseLoginInput) {
  return {
    ...defaultState,
    email: input.email || input.defaultEmail || defaultState.email,
  };
}
/**
 * React hook that controls the data / flow of logging a user in
 */
export function useLogin(input: UseLoginInput = {}) {
  const [formData, setFormData] = useState<LoginData>(getDefaultState(input));
  // We want to always show the email input first, even if a password manager auto
  // fills it for the user. We don't want it skipping to the password page in this case
  const [hasClickedContinue, setHasClickedContinue] = useState(false);
  const [status, setStatus] = useState<LoginStatus>('initial');
  const [formErrors, setErrors] = useState<LoginErrors>({});
  const [submittedFields, setSubmittedFields] = useState<LoginFieldFlags>({
    ...defaultFlagState,
  });
  const [touchedFields, setTouchedFields] = useState<LoginFieldFlags>({
    ...defaultFlagState,
  });
  const authType = useAuthType();

  const isCaptchaRequired = Boolean(formErrors.captcha);

  const routeTo = input.routeTo || defaultRouteTo;

  // Sign-in code step: shown when the password was correct but this browser
  // must be confirmed with an emailed code first. The backend keeps the
  // pending sign-in, so the step never needs the password.
  const newDeviceCodeStep = useNewDeviceCodeStep({
    next: input.next,
    routeTo,
    onConfirmed: () => {
      // Remember email sign-in for next time, as the password step does
      storeAuthType('email');
      setErrors({});
      setStatus('success');
      routeAfterLogin();
    },
    onExpired: message => {
      // Back to the password step with the email kept and the reason shown
      setErrors({ password: message });
      setStatus('error');
    },
    onLeave: () => {
      // Back to the password step with the email kept
      setErrors({});
      setStatus('initial');
    },
  });

  // Only allow users to submit when the email field has something in it
  const canSubmit = formData.email.length > 0;
  const shouldShowPasswordField = shouldShowPassword();
  const shouldShowEmailField = !shouldShowPasswordField;

  function shouldShowPassword() {
    return (
      // If the user has an error with their email, we want to go back to
      // the email input and show it to them
      !formErrors.email &&
      hasClickedContinue &&
      Boolean(
        // If the user has submitted the email
        (submittedFields.email && status === 'initial') ||
          // There is an error (but not with the email field)
          (status === 'error' && !formErrors.email && formErrors.password) ||
          // The user has submitted a password
          (status === 'submitting' && touchedFields.password) ||
          // This catches password managers where the user auto-fills
          // the email and password. We can't use formData.password
          // in case the user deletes the password field completely
          // we don't want it flipping back to the email
          (formData.email && touchedFields.password)
      )
    );
  }

  const shouldShowFacebookSSO = shouldShowSSOOption(
    authType,
    'facebook',
    input.forceLoginDisplayForm,
    input.facebookSSOEnabled
  );
  const shouldShowGoogleSSO = shouldShowSSOOption(
    authType,
    'google',
    input.forceLoginDisplayForm,
    input.googleSSOEnabled
  );
  const shouldShowMicrosoftSSO = shouldShowSSOOption(
    authType,
    'microsoft',
    input.forceLoginDisplayForm,
    input.microsoftSSOEnabled
  );

  const shouldShowSAMLSSO = shouldShowSSOOption(
    authType,
    'saml',
    input.forceLoginDisplayForm,
    input.SAMLSSOEnabled
  );
  const shouldShowEmailPasswordForm =
    input.forceLoginDisplayForm || !authType || authType === 'email';

  useEffect(() => {
    // force the input email if the input changes
    if (input.email || input.defaultEmail) {
      setFormData(data => ({
        ...data,
        email: input.email || input.defaultEmail || '',
      }));
    }
    // If the user entered the captcha on the last handleChange
    // then go ahead and submit the form since there's no
    // submit button visible
    if (formData.captcha) {
      handleSubmit();
    }
    // Adding the handleSubmit would cause this to run on every
    // rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.email, input.defaultEmail, formData.captcha]);

  /**
   * Handles changes in form data
   */
  function handleChange(field: string, value: string) {
    setFormData(data => ({
      ...data,
      // Do not allow field to change if passed in as props
      // @ts-ignore
      [field]: input[field] || value,
    }));
    // Capture that the user has changed this field
    setTouchedFields(state => ({
      ...state,
      [field]: true,
    }));
  }

  function handleClearForm() {
    setFormData(getDefaultState(input));
    setHasClickedContinue(false);
    setErrors({});
    setSubmittedFields({
      ...defaultFlagState,
    });
    setTouchedFields({
      ...defaultFlagState,
    });
    setStatus('initial');
    newDeviceCodeStep.reset();
  }

  /**
   * Routes the user to the next location or the dashboard after signing in
   */
  function routeAfterLogin() {
    if (!input.next || input.next === urls.DEFAULT_LOGIN_NEXT) {
      routeTo(urls.DEFAULT_LOGIN_NEXT);
    } else {
      const redirectUrl = addParamsToUrl(urls.REDIRECT_URL, {
        next: input.next,
        safe_only: true,
        fallback: DASHBOARD_FALLBACK_URL_NAME,
      });
      routeTo(redirectUrl);
    }
  }

  async function handleSubmit(e?: FormEvent<HTMLFormElement>) {
    if (e) {
      e.preventDefault();
    }

    // Show that we are submitting
    setStatus('submitting');
    // Each submit gets a fresh outcome; a challenge is set again below
    newDeviceCodeStep.reset();

    setSubmittedFields({
      captcha: Boolean(formData.captcha),
      email: Boolean(formData.email),
      password: hasClickedContinue && Boolean(formData.password),
    });

    // Attempt login
    try {
      let loginResult;
      if (!hasClickedContinue) {
        loginResult = await login(
          { email: formData.email },
          input.next,
          routeTo
        );
      } else {
        loginResult = await login(formData, input.next, routeTo);
      }

      setHasClickedContinue(true);

      // Bail if we had to redirect and allow the redirect
      // to happen
      if (loginResult.didRedirect) return;

      if (
        loginResult.type ===
          LoginResultType.IS_NEW_DEVICE_CONFIRMATION_REQUIRED &&
        loginResult.newDeviceChallenge
      ) {
        // Not an error the user can fix in the form: the form swaps to the
        // sign-in code step. The password and its used captcha token aren't
        // needed again, so drop them from memory.
        setFormData(data => ({ ...data, password: '', captcha: null }));
        setErrors({});
        setStatus('initial');
        newDeviceCodeStep.start(loginResult.newDeviceChallenge);
        return;
      }

      if (Object.keys(loginResult?.errors || {}).length > 0) {
        // We encountered errors and couldn't log in
        setErrors(loginResult?.errors || {});
        setStatus('error');
      } else {
        // Success
        setErrors({});
        setStatus('success');

        // Route the user to the next location or the dashboard
        routeAfterLogin();
      }
    } catch (err) {
      // This will catch things like network errors and the server being down
      setErrors({ error: GENERIC_ERROR_MESSAGE });
      setStatus('error');
    }
  }

  return {
    // Is the form allowed to be submitted
    canSubmit,
    // Current field data in the form
    formData,
    // Current errors in the form including field level errors and more generic form-level errors
    formErrors,
    // Callback to handle changes to form data
    handleChange,
    // Callback to handle clearing the form and resetting to default state
    handleClearForm,
    // Callback to handle submitting form data to API
    handleSubmit,
    // Captcha input is required when true
    isCaptchaRequired,
    // Sign-in code step; its `challenge` is set when the password was correct
    // but this browser must be confirmed with an emailed code first
    newDeviceCodeStep,
    // Previous login detected from auth_type cookie
    previousAuthType: authType,
    // Should show the email field be shown in the email/password form
    shouldShowEmailField,
    // Should the email/password form be shown
    shouldShowEmailPasswordForm,
    // Should Facebook SSO button be shown
    shouldShowFacebookSSO,
    // Should Google SSO button be shown
    shouldShowGoogleSSO,
    // Should Microsoft SSO button be shown
    shouldShowMicrosoftSSO,
    // Should SAML SSO button be shown
    shouldShowSAMLSSO,
    // Should show the password field be shown in the email/password form
    shouldShowPasswordField,
    // Form status
    status,
    // Fields that have been submitted to the API
    submittedFields,
  };
}
